import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box, Typography, Button, TextField, MenuItem, Stepper, Step, StepLabel, Card, CardContent,
  CircularProgress, Snackbar, Alert, Breadcrumbs, Link, Dialog, DialogTitle, DialogContent
} from '@mui/material';
import HomeIcon from '@mui/icons-material/Home';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import VisibilityIcon from '@mui/icons-material/Visibility';
import SendIcon from '@mui/icons-material/Send';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import api from '../services/api';
import Layout from '../components/Layout';
import FileUpload from '../components/FileUpload';

const NAVY = '#1a237e';
const STEPS = ['Upload PDF & Confirm Customer', 'Contract Details', 'Action'];

export default function NewOffPlatformContract() {
  const { customerId } = useParams();
  const navigate = useNavigate();

  const [customer, setCustomer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [step, setStep] = useState(0);
  const [snack, setSnack] = useState({ open: false, message: '', severity: 'success' });
  const [preview, setPreview] = useState({ open: false, url: null });

  // Step 1 state
  const [contractFiles, setContractFiles] = useState([]); // FileUpload returns array

  // Step 2 state
  const [form, setForm] = useState({
    title: '', contract_amount: '', scope_description: '',
    start_date: '', end_date: '', payment_terms: ''
  });

  // Step 3 state — the created contract
  const [createdContract, setCreatedContract] = useState(null);
  const [defaultPaymentDays, setDefaultPaymentDays] = useState(30);

  // Load customer
  useEffect(() => {
    (async () => {
      try {
        const r = await api.get('/customers');
        const c = (r.data || []).find(x => x.id === customerId);
        if (!c) {
          setSnack({ open: true, message: 'Customer not found', severity: 'error' });
          setTimeout(() => navigate('/customers'), 1500);
          return;
        }
        setCustomer(c);
        setForm(f => ({ ...f, title: 'Contract with ' + c.name }));
        try {
          const pr = await api.get('/sub-prefs');
          const days = pr.data?.default_payment_terms_days ?? 30;
          setDefaultPaymentDays(days);
          const label = days === 0 ? 'Immediate' : ('Net ' + days);
          setForm(f => ({ ...f, payment_terms: label }));
        } catch {}
      } catch {
        setSnack({ open: true, message: 'Failed to load customer', severity: 'error' });
      }
      setLoading(false);
    })();
  }, [customerId, navigate]);

  const setF = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const canNext1 = !!customer;
  const canNext2 = form.contract_amount && Number(form.contract_amount) > 0 && form.payment_terms && form.payment_terms.trim() !== '';

  const handleCreate = async () => {
    if (!canNext2) {
      setSnack({ open: true, message: 'Contract amount is required and must be > 0', severity: 'error' });
      return;
    }
    setBusy(true);
    try {
      const fileUrl = contractFiles.length > 0 ? contractFiles[0].url : null;
      const payload = {
        ...form,
        contract_amount: Number(form.contract_amount),
        contract_file_url: fileUrl
      };
      const r = await api.post('/customers/' + customerId + '/off-platform-contract', payload);
      setCreatedContract(r.data.contract);
      setStep(2);
      setSnack({ open: true, message: 'Contract created!', severity: 'success' });
    } catch (err) {
      setSnack({ open: true, message: err.response?.data?.error || 'Failed to create contract', severity: 'error' });
    }
    setBusy(false);
  };

  const handleMarkSigned = async () => {
    if (!window.confirm('Mark this contract as fully signed? This will activate it and create the PO.')) return;
    setBusy(true);
    try {
      await api.put('/contracts/' + createdContract.id + '/mark-both-signed', { signed_name: 'Paper Signature' });
      setSnack({ open: true, message: 'Contract marked as signed.', severity: 'success' });
      setTimeout(() => navigate('/contracts'), 1200);
    } catch (err) {
      setSnack({ open: true, message: err.response?.data?.error || 'Failed to mark signed', severity: 'error' });
    }
    setBusy(false);
  };

  const handleSendDocuSign = async () => {
    const popup = window.open('about:blank', '_blank');
    setBusy(true);
    try {
      const r = await api.post('/docusign/contracts/' + createdContract.id + '/prepare');
      const senderUrl = r.data?.senderUrl;
      if (!senderUrl) throw new Error('No sender URL returned');
      if (popup) popup.location.href = senderUrl;
      else window.location.href = senderUrl;
      setSnack({ open: true, message: 'Opening DocuSign...', severity: 'success' });
      setTimeout(() => navigate('/contracts'), 1500);
    } catch (err) {
      if (popup) try { popup.close(); } catch {}
      setSnack({ open: true, message: err.response?.data?.error || 'Failed to prepare DocuSign', severity: 'error' });
    }
    setBusy(false);
  };

  if (loading) return <Layout><Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}><CircularProgress /></Box></Layout>;
  if (!customer) return <Layout><Box sx={{ p: 3 }}><Typography>Customer not found.</Typography></Box></Layout>;

  return (
    <Layout>
      <Box sx={{ p: 3, maxWidth: 1000, mx: 'auto' }}>
        <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} sx={{ mb: 2 }}>
          <Link onClick={() => navigate('/customers')} sx={{ cursor: 'pointer', display: 'flex', alignItems: 'center', color: '#666' }}>
            <HomeIcon sx={{ fontSize: 16, mr: 0.5 }} /> Customers
          </Link>
          <Typography sx={{ color: '#666' }}>{customer.name}</Typography>
          <Typography sx={{ color: NAVY, fontWeight: 700 }}>New Contract</Typography>
        </Breadcrumbs>

        <Typography variant="h4" sx={{ fontWeight: 800, color: NAVY, mb: 3 }}>New Contract</Typography>

        <Stepper activeStep={step} sx={{ mb: 4 }}>
          {STEPS.map(s => <Step key={s}><StepLabel>{s}</StepLabel></Step>)}
        </Stepper>

        {/* Step 1 */}
        {step === 0 && (
          <Card sx={{ borderRadius: 3 }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>Customer</Typography>
              <Box sx={{ p: 3, bgcolor: '#f5f6fa', borderRadius: 2, mb: 3 }}>
                <Typography sx={{ fontWeight: 800, color: NAVY, fontSize: 28, lineHeight: 1.2 }}>{customer.name}</Typography>
                {customer.contact_name && <Typography sx={{ color: '#444', fontSize: 16, mt: 0.5 }}>Attn: {customer.contact_name}</Typography>}
                <Typography sx={{ fontWeight: 700, color: '#1a237e', fontSize: 20, mt: 1.5 }}>{customer.address}</Typography>
                <Box sx={{ display: 'flex', gap: 3, mt: 1.5, color: '#666', fontSize: 14 }}>
                  {customer.email && <Typography sx={{ fontSize: 14 }}>{customer.email}</Typography>}
                  {customer.phone && <Typography sx={{ fontSize: 14 }}>{customer.phone}</Typography>}
                </Box>
              </Box>
              <Typography sx={{ color: '#888', fontSize: 12, mb: 2 }}>To edit, go to Customers and update this record.</Typography>

              <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>Contract PDF</Typography>
              <Typography sx={{ color: '#888', fontSize: 13, mb: 2 }}>
                Optional but recommended. Required for DocuSign send.
              </Typography>
              <FileUpload onFilesChange={setContractFiles} existingFiles={contractFiles} />

              <Box sx={{ mt: 3, display: 'flex', justifyContent: 'space-between' }}>
                <Button onClick={() => navigate('/customers')}>Cancel</Button>
                <Button variant="contained" onClick={() => setStep(1)} disabled={!canNext1}
                  sx={{ bgcolor: NAVY, borderRadius: 2 }}>Next</Button>
              </Box>
            </CardContent>
          </Card>
        )}

        {/* Step 2 */}
        {step === 1 && (
          <Card sx={{ borderRadius: 3 }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>Contract Details</Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <TextField label="Title" value={form.title} onChange={(e) => setF('title', e.target.value)} fullWidth />
                <TextField required label="Contract Amount *" type="number" value={form.contract_amount}
                  onChange={(e) => setF('contract_amount', e.target.value)}
                  InputProps={{ startAdornment: <Typography sx={{ mr: 1, color: '#666' }}>$</Typography> }} fullWidth />
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <TextField label="Start Date" type="date" InputLabelProps={{ shrink: true }}
                    value={form.start_date} onChange={(e) => setF('start_date', e.target.value)} fullWidth />
                  <TextField label="End Date" type="date" InputLabelProps={{ shrink: true }}
                    value={form.end_date} onChange={(e) => setF('end_date', e.target.value)} fullWidth />
                </Box>
                <TextField label="Scope Description" value={form.scope_description}
                  onChange={(e) => setF('scope_description', e.target.value)} fullWidth multiline rows={3} />
                <TextField select required label="Payment Terms *" value={form.payment_terms || ''}
                  onChange={(e) => {
                    const v = e.target.value;
                    if (v === '__CUSTOM__') { setF('payment_terms', ''); }
                    else { setF('payment_terms', v); }
                  }} fullWidth>
                  {['Immediate','Net 7','Net 15','Net 30','__CUSTOM__'].map(opt => (
                    <MenuItem key={opt} value={opt}>{opt === '__CUSTOM__' ? 'Custom — enter below' : opt}</MenuItem>
                  ))}
                </TextField>
                {!['Immediate','Net 7','Net 15','Net 30'].includes(form.payment_terms) && (
                  <TextField required label="Custom Payment Terms *" value={form.payment_terms || ''}
                    onChange={(e) => setF('payment_terms', e.target.value)} fullWidth multiline rows={2}
                    placeholder="e.g. 50% upfront, 50% on completion" />
                )}
              </Box>
              <Box sx={{ mt: 3, display: 'flex', justifyContent: 'space-between' }}>
                <Button onClick={() => setStep(0)} disabled={busy}>Back</Button>
                <Button variant="contained" onClick={handleCreate} disabled={busy || !canNext2}
                  sx={{ bgcolor: NAVY, borderRadius: 2 }}>
                  {busy ? 'Creating...' : 'Create Contract'}
                </Button>
              </Box>
            </CardContent>
          </Card>
        )}

        {/* Step 3 */}
        {step === 2 && createdContract && (
          <Card sx={{ borderRadius: 3 }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>Contract Created</Typography>
              <Box sx={{ p: 2, bgcolor: '#e8f5e9', borderRadius: 2, mb: 3 }}>
                <Typography sx={{ color: '#2e7d32', fontWeight: 700 }}>
                  {createdContract.contract_number || createdContract.id?.slice(0, 8)}
                </Typography>
                <Typography sx={{ color: '#555', mt: 0.5 }}>{createdContract.title}</Typography>
                <Typography sx={{ color: NAVY, fontWeight: 800, fontSize: 22, mt: 1 }}>
                  ${Number(createdContract.contract_amount).toLocaleString()}
                </Typography>
              </Box>

              <Typography sx={{ fontWeight: 700, mb: 2 }}>What's next?</Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                {createdContract.contract_file_url && (
                  <Button variant="outlined" startIcon={<VisibilityIcon />} disabled={busy}
                    onClick={() => setPreview({ open: true, url: createdContract.contract_file_url })}
                    sx={{ borderRadius: 2 }}>
                    View Contract
                  </Button>
                )}
                <Button variant="contained" startIcon={<SendIcon />} disabled={busy || !createdContract.contract_file_url}
                  onClick={handleSendDocuSign}
                  sx={{ bgcolor: '#6a1b9a', borderRadius: 2 }}>
                  Send via DocuSign
                </Button>
                <Button variant="outlined" startIcon={<CheckCircleIcon />} disabled={busy}
                  onClick={handleMarkSigned}
                  sx={{ borderRadius: 2, borderColor: '#2e7d32', color: '#2e7d32' }}>
                  Mark as Signed
                </Button>

              </Box>
              {!createdContract.contract_file_url && (
                <Typography sx={{ color: '#999', fontSize: 12, mt: 2 }}>
                  Tip: DocuSign requires a PDF. Use "Replace PDF" on the contract row in Contracts page to attach one later.
                </Typography>
              )}
            </CardContent>
          </Card>
        )}

        {/* PDF preview dialog */}
        <Dialog open={preview.open} onClose={() => setPreview({ open: false, url: null })} fullScreen>
          <DialogTitle sx={{ bgcolor: NAVY, color: 'white', display: 'flex', justifyContent: 'space-between' }}>
            Contract PDF
            <Button onClick={() => setPreview({ open: false, url: null })} sx={{ color: 'white' }}>Close</Button>
          </DialogTitle>
          <DialogContent sx={{ p: 0 }}>
            {preview.url && <iframe src={preview.url} style={{ width: '100%', height: '100%', border: 'none' }} title="contract" />}
          </DialogContent>
        </Dialog>

        <Snackbar open={snack.open} autoHideDuration={4000} onClose={() => setSnack(s => ({ ...s, open: false }))}>
          <Alert severity={snack.severity}>{snack.message}</Alert>
        </Snackbar>
      </Box>
    </Layout>
  );
}
